import { useApiPost, useApiQuery } from '@/api/main/query'
import { PaginatedCollection } from '@/api/types'
import { Currencies } from '@/entities/curency'
import { SortDirection } from '@/entities/sort'
import { DEFAULT_TAKE } from '@/shared/constants/constants'
import { useDebounce, useToast } from '@/shared/hooks'
import { useState } from 'react'
import { withdrawsAPITrader } from '../api'
import { useQueryClient } from '@tanstack/react-query'
import { Roles } from '@/entities/roles'
import { USER_ROLE_KEY } from '@/features/auth'
import { getStorageItem } from '@/shared/lib/storage/localStorage'
import {
  TraderBalanceAndChannelsModel,
  ConfirmSendData,
  TraderWithdrawFilterStatus,
} from '../model'

type UseWithdrawsTraderProps = {
  status?: TraderWithdrawFilterStatus
  onSuccessDeclineCallback?: () => void
  debouncedSearch?: string
  startDate?: string
  endDate?: string
  sortCreatedAt?: SortDirection
  sortAmountTo?: SortDirection
  sortAllowedAt?: SortDirection
  take?: number
}

// Тип для старого API (первый файл)
type LegacyConfirmSendData = {
  sentAmount:number
  withdrawId: string
  recieptFileAttacment?: {
    originalFileName: string
    base64FileData: string
  }
}

// Тип для нового API (дополнительные файлы)
type AttachReceiptData = {
  withdrawId: string
  receiptFileAttachment: {
    originalFileName: string
    base64FileData: string
  }
}

export const useWithdrawsTrader = ({
  status = TraderWithdrawFilterStatus.All,
  onSuccessDeclineCallback,
  debouncedSearch,
  startDate,
  endDate,
  sortCreatedAt,
  sortAmountTo,
  sortAllowedAt,
  take = DEFAULT_TAKE,
}: UseWithdrawsTraderProps = {}) => {
  const [skip, setSkip] = useState(0)
  const [loadingAssignedIds, setLoadingAssignedIds] = useState<string[]>([])
  const { addToast } = useToast()
  const queryClient = useQueryClient()
  const userRole = getStorageItem<Roles | null>(USER_ROLE_KEY, null)
  const shouldFetch = userRole === Roles.Trader

  // debounce для пагинации
  const debouncedSkip = useDebounce(skip, 500)
  // если skip = 0 → сразу 0, иначе используем задебоушенный skip
  const effectiveSkip = skip === 0 ? 0 : debouncedSkip

  // Баланс и каналы вывода
  const {
    data: balanceChannelsData,
    isLoading: isBalanceChannelsLoading,
    isError: balanceChannelsError,
  } = useApiQuery<TraderBalanceAndChannelsModel>(
    ['traderBalanceChannels'],
    withdrawsAPITrader.getChannels({ country: 'ru', currency: Currencies.RUB }),
    undefined,
    { enabled: shouldFetch }
  )

  // Единый запрос для всех статусов через новый API
  // Нормализуем значения для queryKey (преобразуем пустые строки в undefined)
  const normalizedSearch = debouncedSearch || undefined
  const normalizedStartDate = startDate || undefined
  const normalizedEndDate = endDate || undefined

  const queryParams = {
    status,
    skip: effectiveSkip,
    take,
    ...(normalizedSearch && { search: normalizedSearch }),
    ...(normalizedStartDate && { startDate: normalizedStartDate }),
    ...(normalizedEndDate && { endDate: normalizedEndDate }),
    ...(sortCreatedAt !== undefined && { sortCreatedAt }),
    ...(sortAmountTo !== undefined && { sortAmountTo }),
    ...(sortAllowedAt !== undefined && { sortAllowedAt }),
  }

  const {
    data: withdrawsData,
    error: withdrawsError,
    isLoading: isWithdrawsLoading,
  } = useApiQuery<PaginatedCollection<any[]>>(
    [
      'withdraws',
      status,
      effectiveSkip,
      take,
      normalizedSearch,
      normalizedStartDate,
      normalizedEndDate,
      sortCreatedAt,
      sortAmountTo,
      sortAllowedAt
    ],
    withdrawsAPITrader.list,
    queryParams,
    { enabled: shouldFetch, refetchInterval: 60000 }
  )

  // Взять заявку в работу
  const acceptWithdrawMutation = useApiPost<null, { withdrawId: string }>(
    withdrawsAPITrader.acceptWithdraw,
    {
      onSuccess: () => {
        addToast('Успех!', 'Заявка принята в работу.', 'success')

        queryClient.invalidateQueries({
          predicate: (query) =>
            typeof query.queryKey[0] === 'string' && query.queryKey[0] === 'withdraws',
        })
      },
      onError: (error) => {
        addToast('Ошибка!', error.message, 'error')
      },
      onSettled: (_data, _error, variables) => {
        setLoadingAssignedIds((prev) => prev.filter((x) => x !== variables.withdrawId))
      },
    }
  )

  const acceptWithdraw = (id: string) => {
    setLoadingAssignedIds((prev) => [...prev, id])
    acceptWithdrawMutation.mutate({ withdrawId: id })
  }

  // подтвердить оплату заявки (старый API для первого файла)
  const { mutateAsync: confirmWithdrawMutation, isPending: isConfirmWithdrawLoading } = useApiPost<
    void,
    LegacyConfirmSendData
  >(withdrawsAPITrader.confirmPayment, {
    onSuccess: () => addToast('Успех!', 'Заявка оплачена.', 'success'),
    onError: (error) => {
      addToast('Ошибка!', error.message, 'error')
    },
  })

  // прикрепить дополнительные чеки (новый API)
  const { mutateAsync: attachReceiptMutation, isPending: isAttachReceiptLoading } = useApiPost<
    void,
    AttachReceiptData
  >(withdrawsAPITrader.attachReceipt, {
    onError: (error) => {
      addToast('Ошибка!', error.message, 'error')
    },
  })

  const confirmWithdrawPayment = async (data: ConfirmSendData, onSuccessCallback?: () => void) => {
    const {sentAmount, withdrawId, recieptFileAttacments = [] } = data

    let isConfirmed = false

    try {
      // Первый файл отправляем через старый API
      const firstFile = recieptFileAttacments[0]
      const legacyData: LegacyConfirmSendData = {
        sentAmount,
        withdrawId,
        recieptFileAttacment: firstFile,
      }

      await confirmWithdrawMutation(legacyData)
      isConfirmed = true

      // Если есть дополнительные файлы, отправляем их через новый API с задержкой
      if (recieptFileAttacments.length > 1) {
        const additionalFiles = recieptFileAttacments.slice(1)

        for (let i = 0; i < additionalFiles.length; i++) {
          // Добавляем задержку 500ms перед каждым дополнительным файлом
          if (i > 0) {
            await new Promise((resolve) => setTimeout(resolve, 500))
          }

          const file = additionalFiles[i]
          const attachData: AttachReceiptData = {
            withdrawId,
            receiptFileAttachment: file,
          }

          await attachReceiptMutation(attachData)

          // Задержка после отправки каждого файла
          await new Promise((resolve) => setTimeout(resolve, 500))
        }

        if (additionalFiles.length > 0) {
          addToast(
            'Успех!',
            `Прикреплено дополнительно чеков: ${additionalFiles.length}`,
            'success'
          )
        }
      }

      // Обновляем кэш запросов
      await queryClient.invalidateQueries({ queryKey: ['withdraws'] })
    } catch (error) {
      console.error('Ошибка при подтверждении оплаты:', error)
    }

    if (isConfirmed) {
      onSuccessCallback?.()
    }
  }

  // отменить заявку
  const { mutate: declineWithdrawMutation, isPending: isCancelWithdrawLoading } = useApiPost<
    void,
    {
      withdrawId: string
      traderReason: string
    }
  >(withdrawsAPITrader.declineWithdraw, {
    onSuccess: () => {
      queryClient.invalidateQueries({
        predicate: (query) =>
          typeof query.queryKey[0] === 'string' && query.queryKey[0] === 'withdraws',
      })
      if (onSuccessDeclineCallback) onSuccessDeclineCallback()
    },
    onError: (error) => {
      addToast('Ошибка!', error.message, 'error')
    },
  })

  const declineWithdraw = (data: { withdrawId: string; traderReason: string }) => {
    declineWithdrawMutation(data)
  }

  //пагинация
  const handlePageChange = (newSkip: number) => setSkip(newSkip)
  const resetPage = () => setSkip(0)

  return {
    data: {
      balanceChannelsData,
      withdrawsData,
      effectiveSkip,
    },
    loading: {
      isBalanceChannelsLoading,
      isWithdrawsLoading,
      isConfirmWithdrawLoading: isConfirmWithdrawLoading || isAttachReceiptLoading,
      isCancelWithdrawLoading,
    },
    errors: {
      balanceChannelsError,
      withdrawsError,
    },
    methods: {
      handlePageChange,
      acceptWithdraw,
      confirmWithdrawPayment,
      declineWithdraw,
      resetPage,
    },
    ui: {
      loadingAssignedIds,
    },
  }
}